import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiErrorMessage = (error: any): string => {
  const data = error?.response?.data;
  const message = data?.message || data?.error?.message || data?.error || error?.message;
  if (Array.isArray(message)) return message.join('; ');
  return typeof message === 'string' ? message : 'Request failed';
};

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface WorkCenter {
  id: string;
  code: string;
  name: string;
}

export interface Operator {
  id: string;
  employeeId: string;
  name: string;
  department: string;
  workCenterId: string;
  workCenterCode: string;
  workCenterName: string;
  sessionId?: string;
}

export interface WorkOrderOperation {
  /** Work order operation UUID */
  id: string;
  /** Work order UUID */
  workOrderId: string;
  orderNumber: string;
  productCode: string;
  productName: string;
  itemId?: string;
  routingId?: string;
  operationNumber: string;
  operationDescription: string;
  operationSequence?: number;
  workCenter: string;
  workCenterCode?: string;
  instructions?: string;
  plannedQty: number;
  producedQty: number;
  scrapQty: number;
  reworkQty: number;
  status: 'queued' | 'started' | 'paused' | 'completed' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: string;
  dueDate: string;
  estimatedHours: number;
  startedAt?: string;
  pausedAt?: string;
  completedAt?: string;
  notes?: string;
}

export interface MaterialRequirement {
  id: string;
  itemCode: string;
  itemName: string;
  requiredQty: number;
  consumedQty: number;
  uom: string;
  lotNumber?: string;
  isShort: boolean;
}

export interface QualityCheckpoint {
  id: string;
  name: string;
  type: 'pass_fail' | 'measurement';
  specification?: string;
  uom?: string;
  target?: number;
  lowerLimit?: number;
  upperLimit?: number;
  method?: string;
  result?: 'pass' | 'fail';
  measuredValue?: string;
}

export interface QualityPlanSummary {
  id: string;
  planName: string;
  inspectionType: string;
  sampleSize: number;
  revision?: string;
}

export interface Gauge {
  id: string;
  gaugeCode: string;
  gaugeName: string;
  status: string;
}

export interface WorkInstructionStep {
  id: string;
  stepNumber: number;
  title: string;
  description: string;
  isCompleted: boolean;
  imageUrl?: string;
}

export interface OperatorState {
  workCenters: WorkCenter[];
  currentOperator: Operator | null;
  assignedWorkOrders: WorkOrderOperation[];
  activeOperation: WorkOrderOperation | null;
  activeExecutionId: string | null;
  operationElapsedSeconds: number;
  isTimerRunning: boolean;
  materialRequirements: MaterialRequirement[];
  qualityPlan: QualityPlanSummary | null;
  qualityCheckpoints: QualityCheckpoint[];
  /** Operation UUID that carries the active control plan (route operation for seeded routes) */
  effectiveOperationId: string | null;
  gauges: Gauge[];
  selectedGaugeCode: string | null;
  lastInspection: { id: string; approvalStatus: string; overallResult: string } | null;
  workInstructionSteps: WorkInstructionStep[];
  gateBlockers: string[];
  isLoading: boolean;
  isSubmittingQuality: boolean;
  error: string | null;
}

// ─── Initial State ─────────────────────────────────────────────────────────────

const initialState: OperatorState = {
  workCenters: [],
  currentOperator: null,
  assignedWorkOrders: [],
  activeOperation: null,
  activeExecutionId: null,
  operationElapsedSeconds: 0,
  isTimerRunning: false,
  materialRequirements: [],
  qualityPlan: null,
  qualityCheckpoints: [],
  effectiveOperationId: null,
  gauges: [],
  selectedGaugeCode: null,
  lastInspection: null,
  workInstructionSteps: [],
  gateBlockers: [],
  isLoading: false,
  isSubmittingQuality: false,
  error: null,
};

// ─── Mapping helpers ───────────────────────────────────────────────────────────

const mapOperationStatus = (status: string | undefined): WorkOrderOperation['status'] => {
  const value = (status || '').toLowerCase();
  if (value === 'in_progress' || value === 'started') return 'started';
  if (value === 'paused') return 'paused';
  if (value === 'completed') return 'completed';
  if (value === 'on_hold' || value === 'hold') return 'on_hold';
  return 'queued';
};

const mapPriority = (priority: string | undefined): WorkOrderOperation['priority'] => {
  const value = (priority || '').toLowerCase();
  if (value === 'urgent' || value === 'critical') return 'urgent';
  if (value === 'high') return 'high';
  if (value === 'low') return 'low';
  return 'medium';
};

const toWorkOrderOperations = (workOrder: any, workCenterCode: string): WorkOrderOperation[] => {
  const operations: any[] = workOrder.operations || [];
  return operations
    .filter(
      (op) =>
        !workCenterCode ||
        (op.workCenterCode || '').toUpperCase() === workCenterCode.toUpperCase(),
    )
    .filter((op) => mapOperationStatus(op.status) !== 'completed')
    .map((op) => {
      const plannedQty = Number(op.quantityPlanned ?? workOrder.quantityPlanned ?? 0);
      const runMinutes =
        Number(op.setupTimePlanned ?? 0) + Number(op.runTimePlanned ?? 0) * plannedQty;
      return {
        id: op.id,
        workOrderId: workOrder.id,
        orderNumber: workOrder.workOrderNumber,
        productCode: workOrder.itemCode || '',
        productName: workOrder.itemDescription || workOrder.itemCode || '',
        itemId: workOrder.itemId,
        routingId: workOrder.routingId,
        operationNumber: op.operationCode || `OP-${op.operationSequence}`,
        operationDescription: op.operationDescription || op.operationName || op.operationCode || 'Operation',
        operationSequence: op.operationSequence,
        workCenter: op.workCenterCode || '',
        workCenterCode: op.workCenterCode,
        instructions: op.instructions,
        plannedQty,
        producedQty: Number(op.quantityCompleted ?? 0),
        scrapQty: Number(op.quantityScrapped ?? 0),
        reworkQty: 0,
        status: mapOperationStatus(op.status),
        priority: mapPriority(workOrder.priority),
        startDate: op.actualStartTime || workOrder.plannedStartDate || new Date().toISOString(),
        dueDate: workOrder.dueDate || workOrder.plannedEndDate || new Date().toISOString(),
        estimatedHours: Math.round((runMinutes / 60) * 10) / 10,
        startedAt: op.actualStartTime,
        notes: undefined,
      } as WorkOrderOperation;
    });
};

const instructionsToSteps = (instructions?: string): WorkInstructionStep[] => {
  if (!instructions) return [];
  return instructions
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line, index) => {
      const withoutNumber = line.replace(/^\d+[.)]\s*/, '');
      return {
        id: `step-${index + 1}`,
        stepNumber: index + 1,
        title: withoutNumber.length > 60 ? `${withoutNumber.slice(0, 60)}…` : withoutNumber,
        description: withoutNumber,
        isCompleted: false,
      };
    });
};

const planParametersToCheckpoints = (plan: any): QualityCheckpoint[] => {
  const parameters: any[] = plan?.acceptanceCriteria?.parameters || [];
  return parameters.map((p, index) => {
    const isAttribute = p.type === 'attribute';
    return {
      id: `cp-${index + 1}`,
      name: p.name,
      type: isAttribute ? 'pass_fail' : 'measurement',
      specification: isAttribute
        ? p.expected || 'Conforms'
        : [
            p.target !== undefined && p.target !== null ? `target ${p.target}` : null,
            p.lowerSpec !== undefined && p.lowerSpec !== null ? `LSL ${p.lowerSpec}` : null,
            p.upperSpec !== undefined && p.upperSpec !== null ? `USL ${p.upperSpec}` : null,
            p.unit || null,
          ]
            .filter(Boolean)
            .join(' / '),
      uom: p.unit,
      target: p.target ?? undefined,
      lowerLimit: p.lowerSpec ?? undefined,
      upperLimit: p.upperSpec ?? undefined,
      method: p.method,
    } as QualityCheckpoint;
  });
};

// ─── Async Thunks ──────────────────────────────────────────────────────────────

export const fetchWorkCenters = createAsyncThunk(
  'operator/fetchWorkCenters',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/master-data/work-centers`, {
        headers: authHeaders(),
      });
      const raw: any[] = Array.isArray(response.data) ? response.data : response.data?.data || [];
      return raw.map((wc) => ({
        id: wc.id,
        code: wc.workCenterCode || wc.code,
        name: wc.name || wc.workCenterCode,
      })) as WorkCenter[];
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const loginOperator = createAsyncThunk(
  'operator/login',
  async (
    { badgeCode, workCenter }: { badgeCode: string; workCenter: WorkCenter },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/shopfloor/operators/badge-login`,
        { badgeCode, workCenterCode: workCenter.code, deviceId: 'operator-workstation' },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      const session = response.data?.data || response.data;
      if (session?.sessionId) {
        localStorage.setItem('operatorSessionToken', session.sessionId);
      }
      return {
        id: session.operatorId,
        employeeId: session.operatorCode || badgeCode,
        name: session.operatorName || `Operator ${badgeCode}`,
        department: 'Production',
        workCenterId: workCenter.id,
        workCenterCode: workCenter.code,
        workCenterName: workCenter.name,
        sessionId: session.sessionId || session.id,
      } as Operator;
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const fetchAssignedWorkOrders = createAsyncThunk(
  'operator/fetchAssignedWorkOrders',
  async (workCenterCode: string, { rejectWithValue }) => {
    try {
      const statuses = ['released', 'started', 'in_progress'];
      const responses = await Promise.all(
        statuses.map((status) =>
          axios
            .get(`${API_BASE_URL}/work-orders`, {
              params: { status, limit: 50 },
              headers: authHeaders(),
            })
            .then((r) => (r.data?.data || []) as any[])
            .catch(() => [] as any[])
        )
      );
      const seen = new Set<string>();
      const workOrders = responses.flat().filter((wo) => {
        if (seen.has(wo.id)) return false;
        seen.add(wo.id);
        return true;
      });
      return workOrders.flatMap((wo) => toWorkOrderOperations(wo, workCenterCode));
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

/**
 * Loads everything the operator needs for the selected operation from the
 * backend: material requirements, the active quality control plan (checking
 * both the work-order operation ID and the underlying route operation ID),
 * the gauge register, and work instructions.
 */
export const loadOperationContext = createAsyncThunk(
  'operator/loadOperationContext',
  async (wo: WorkOrderOperation, { rejectWithValue }) => {
    try {
      const headers = authHeaders();

      // Material requirements (best-effort — endpoint shape varies)
      let materials: MaterialRequirement[] = [];
      try {
        const materialResponse = await axios.get(
          `${API_BASE_URL}/work-orders/${wo.workOrderId}/material-summary`,
          { headers }
        );
        const rawMaterials: any[] = Array.isArray(materialResponse.data?.data)
          ? materialResponse.data.data
          : materialResponse.data?.data?.materials || [];
        materials = rawMaterials.map((m, index) => {
          const required = Number(m.requiredQuantity ?? m.plannedQuantity ?? m.quantityRequired ?? 0);
          const consumed = Number(m.consumedQuantity ?? m.actualQuantity ?? m.quantityConsumed ?? 0);
          return {
            id: m.id || `mat-${index}`,
            itemCode: m.itemCode || m.materialCode || m.productCode || '—',
            itemName: m.itemDescription || m.materialName || m.productName || m.itemCode || 'Material',
            requiredQty: required,
            consumedQty: consumed,
            uom: m.unitOfMeasure || m.uom || '',
            lotNumber: m.lotNumber,
            isShort: consumed < required,
          } as MaterialRequirement;
        });
      } catch {
        materials = [];
      }

      // Active control plan: try the WO operation ID first, then the route operation
      const fetchPlans = async (operationId: string): Promise<any[]> => {
        const response = await axios.get(
          `${API_BASE_URL}/quality/control-plans/operations/${operationId}/active`,
          { headers }
        );
        return response.data?.data || [];
      };

      let effectiveOperationId = wo.id;
      let plans: any[] = [];
      try {
        plans = await fetchPlans(wo.id);
      } catch {
        plans = [];
      }
      if (plans.length === 0 && wo.routingId) {
        try {
          const routeResponse = await axios.get(
            `${API_BASE_URL}/work-orders/process-routes/${wo.routingId}`,
            { headers }
          );
          const route = routeResponse.data?.data || routeResponse.data;
          const routeOp = (route?.operations || []).find(
            (op: any) =>
              op.sequence === wo.operationSequence ||
              (op.operationCode || '').toUpperCase() === wo.operationNumber.toUpperCase()
          );
          if (routeOp) {
            const routePlans = await fetchPlans(routeOp.id);
            if (routePlans.length > 0) {
              plans = routePlans;
              effectiveOperationId = routeOp.id;
            }
          }
        } catch {
          // keep empty plans — quality tab will show "no control plan"
        }
      }

      const plan = plans.find((p) => p.inspectionType === 'in_process') || plans[0] || null;

      // Gauge register (IMTP) — only in-calibration gauges are selectable
      let gauges: Gauge[] = [];
      try {
        const gaugeResponse = await axios.get(`${API_BASE_URL}/quality/gauges`, {
          params: { status: 'active' },
          headers,
        });
        gauges = (gaugeResponse.data?.data || []).map((g: any) => ({
          id: g.id,
          gaugeCode: g.gaugeCode,
          gaugeName: g.gaugeName,
          status: g.status,
        }));
      } catch {
        gauges = [];
      }

      return {
        materials,
        plan: plan
          ? ({
              id: plan.id,
              planName: plan.planName,
              inspectionType: plan.inspectionType,
              sampleSize: plan.sampleSize || 1,
              revision: plan.revision,
            } as QualityPlanSummary)
          : null,
        checkpoints: plan ? planParametersToCheckpoints(plan) : [],
        effectiveOperationId,
        gauges,
        steps: instructionsToSteps(wo.instructions),
      };
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const startOperation = createAsyncThunk(
  'operator/startOperation',
  async (_, { getState, rejectWithValue }) => {
    const state = (getState() as any).operator as OperatorState;
    const wo = state.activeOperation;
    const operator = state.currentOperator;
    if (!wo || !operator) return rejectWithValue('No active operation selected');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/shopfloor/workorders/${wo.workOrderId}/start`,
        {
          operationId: state.effectiveOperationId || wo.id,
          workCenterId: operator.workCenterId,
          plannedQuantity: wo.plannedQty,
          operators: [
            {
              operatorId: operator.id,
              operatorCode: operator.employeeId,
              operatorName: operator.name,
              isPrimary: true,
              sessionId: operator.sessionId,
            },
          ],
          metadata: {
            workCenterCode: operator.workCenterCode,
            workOrderNumber: wo.orderNumber,
            operationCode: wo.operationNumber,
          },
        },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      const execution = response.data?.data;
      return { opId: wo.id, executionId: execution?.id as string };
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const pauseOperation = createAsyncThunk(
  'operator/pauseOperation',
  async (_, { getState, rejectWithValue }) => {
    const state = (getState() as any).operator as OperatorState;
    const wo = state.activeOperation;
    if (!wo) return rejectWithValue('No active operation selected');
    try {
      await axios.post(
        `${API_BASE_URL}/shopfloor/workorders/${wo.workOrderId}/pause`,
        { reason: 'Paused from operator workstation' },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      return { opId: wo.id };
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const updateProductionProgress = createAsyncThunk(
  'operator/updateProgress',
  async (
    {
      producedQty,
      scrapQty,
      reworkQty,
      notes,
    }: { producedQty: number; scrapQty: number; reworkQty: number; notes?: string },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as any).operator as OperatorState;
    const wo = state.activeOperation;
    if (!wo) return rejectWithValue('No active operation selected');
    try {
      await axios.put(
        `${API_BASE_URL}/shopfloor/workorders/${wo.workOrderId}/progress`,
        {
          executionId: state.activeExecutionId,
          completedQuantity: producedQty,
          producedQty,
          scrapQuantity: scrapQty,
          scrapQty,
          reworkQuantity: reworkQty,
          reworkQty,
          notes,
        },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      return { opId: wo.id, producedQty, scrapQty, reworkQty };
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

export const completeOperation = createAsyncThunk(
  'operator/completeOperation',
  async (
    {
      producedQty,
      scrapQty,
      reworkQty,
      overrideQualityGate,
      overrideReason,
    }: {
      producedQty: number;
      scrapQty: number;
      reworkQty: number;
      overrideQualityGate?: boolean;
      overrideReason?: string;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as any).operator as OperatorState;
    const wo = state.activeOperation;
    const operator = state.currentOperator;
    if (!wo || !operator) return rejectWithValue('No active operation selected');
    if (!state.activeExecutionId) {
      return rejectWithValue('Operation was not started from this terminal — press START first');
    }
    try {
      await axios.put(
        `${API_BASE_URL}/shopfloor/executions/${state.activeExecutionId}/complete`,
        { completedBy: operator.id, overrideQualityGate, overrideReason },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      return { opId: wo.id, producedQty, scrapQty, reworkQty };
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

/**
 * Submits the quality checklist as a real inspection in the quality service:
 * creates the inspection point against the active control plan, records the
 * per-characteristic results (with the selected IMTP gauge), and applies the
 * operator's electronic signature. QC approval then happens on the Quality page.
 */
export const submitQualityInspection = createAsyncThunk(
  'operator/submitQualityInspection',
  async (_, { getState, rejectWithValue }) => {
    const state = (getState() as any).operator as OperatorState;
    const wo = state.activeOperation;
    const operator = state.currentOperator;
    const plan = state.qualityPlan;
    if (!wo || !operator) return rejectWithValue('No active operation selected');
    if (!plan) return rejectWithValue('No active control plan for this operation');

    const headers = { ...authHeaders(), 'Content-Type': 'application/json' };
    try {
      const createResponse = await axios.post(
        `${API_BASE_URL}/quality/inspection-points`,
        {
          workOrderId: wo.workOrderId,
          operationId: state.effectiveOperationId || wo.id,
          qcPlanId: plan.id,
          inspectionDate: new Date().toISOString(),
          inspectorId: operator.id,
          batchNumber: wo.orderNumber,
          lotNumber: wo.orderNumber,
          sampleSize: plan.sampleSize,
        },
        { headers }
      );
      const inspectionId = createResponse.data?.data?.id;

      const testResults = state.qualityCheckpoints.map((cp) => {
        if (cp.type === 'measurement') {
          const measured = Number(cp.measuredValue);
          return {
            testParameter: cp.name,
            measuredValue: Number.isFinite(measured) ? measured : 0,
            unitOfMeasure: cp.uom || 'unit',
            targetValue: cp.target ?? 0,
            lowerLimit: cp.lowerLimit ?? 0,
            upperLimit: cp.upperLimit ?? 0,
            result: cp.result || 'fail',
            measurementEquipment: state.selectedGaugeCode || undefined,
          };
        }
        // Attribute checks use a 1/0 convention (1 = conforms)
        return {
          testParameter: cp.name,
          measuredValue: cp.result === 'pass' ? 1 : 0,
          unitOfMeasure: 'attr',
          targetValue: 1,
          lowerLimit: 1,
          upperLimit: 1,
          result: cp.result || 'fail',
        };
      });

      const anyFail = state.qualityCheckpoints.some((cp) => cp.result === 'fail');
      await axios.post(
        `${API_BASE_URL}/quality/inspection-points/${inspectionId}/record`,
        {
          inspectedQuantity: plan.sampleSize,
          passedQuantity: anyFail ? 0 : plan.sampleSize,
          failedQuantity: anyFail ? plan.sampleSize : 0,
          overallResult: anyFail ? 'fail' : 'pass',
          testResults,
          notes: `Recorded at operator workstation by ${operator.name} (${operator.employeeId})`,
        },
        { headers }
      );

      const signResponse = await axios.post(
        `${API_BASE_URL}/quality/inspection-points/${inspectionId}/sign-off`,
        { level: 'operator', signedBy: operator.id },
        { headers }
      );

      return {
        id: inspectionId as string,
        approvalStatus: signResponse.data?.data?.approvalStatus || 'operator_signed',
        overallResult: anyFail ? 'fail' : 'pass',
      };
    } catch (error: any) {
      return rejectWithValue(apiErrorMessage(error));
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const operatorSlice = createSlice({
  name: 'operator',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    logoutOperator: (state) => {
      state.currentOperator = null;
      state.assignedWorkOrders = [];
      state.activeOperation = null;
      state.activeExecutionId = null;
      state.operationElapsedSeconds = 0;
      state.isTimerRunning = false;
      state.materialRequirements = [];
      state.qualityPlan = null;
      state.qualityCheckpoints = [];
      state.workInstructionSteps = [];
      state.gateBlockers = [];
      state.lastInspection = null;
    },
    selectWorkOrder: (state, action: PayloadAction<WorkOrderOperation>) => {
      state.activeOperation = action.payload;
      state.activeExecutionId = null;
      state.gateBlockers = [];
      state.lastInspection = null;
      // Real context (materials, control plan, gauges, instructions) is loaded
      // by the loadOperationContext thunk dispatched alongside this action.
      state.materialRequirements = [];
      state.qualityPlan = null;
      state.qualityCheckpoints = [];
      state.workInstructionSteps = [];
      state.effectiveOperationId = action.payload.id;
      state.isTimerRunning = action.payload.status === 'started';
    },
    clearActiveOperation: (state) => {
      state.activeOperation = null;
      state.activeExecutionId = null;
      state.operationElapsedSeconds = 0;
      state.isTimerRunning = false;
      state.gateBlockers = [];
    },
    tickTimer: (state) => {
      if (state.isTimerRunning) {
        state.operationElapsedSeconds += 1;
      }
    },
    resetTimer: (state) => {
      state.operationElapsedSeconds = 0;
    },
    setSelectedGauge: (state, action: PayloadAction<string | null>) => {
      state.selectedGaugeCode = action.payload;
    },
    clearGateBlockers: (state) => {
      state.gateBlockers = [];
    },
    updateQualityCheckpoint: (
      state,
      action: PayloadAction<{ id: string; result?: 'pass' | 'fail'; measuredValue?: string }>
    ) => {
      const checkpoint = state.qualityCheckpoints.find((c) => c.id === action.payload.id);
      if (!checkpoint) return;
      if (action.payload.result !== undefined) checkpoint.result = action.payload.result;
      if (action.payload.measuredValue !== undefined) {
        checkpoint.measuredValue = action.payload.measuredValue;
        // Auto-evaluate measurements against spec limits; operator can override
        const measured = Number(action.payload.measuredValue);
        if (
          Number.isFinite(measured) &&
          checkpoint.type === 'measurement' &&
          (checkpoint.lowerLimit !== undefined || checkpoint.upperLimit !== undefined)
        ) {
          const belowLower =
            checkpoint.lowerLimit !== undefined && measured < checkpoint.lowerLimit;
          const aboveUpper =
            checkpoint.upperLimit !== undefined && measured > checkpoint.upperLimit;
          checkpoint.result = belowLower || aboveUpper ? 'fail' : 'pass';
        }
      }
    },
    toggleWorkInstructionStep: (state, action: PayloadAction<string>) => {
      const step = state.workInstructionSteps.find((s) => s.id === action.payload);
      if (step) {
        step.isCompleted = !step.isCompleted;
      }
    },
    consumeMaterial: (
      state,
      action: PayloadAction<{ id: string; qty: number; lotNumber?: string }>
    ) => {
      const material = state.materialRequirements.find((m) => m.id === action.payload.id);
      if (material) {
        material.consumedQty = Math.min(
          material.consumedQty + action.payload.qty,
          material.requiredQty
        );
        if (action.payload.lotNumber) {
          material.lotNumber = action.payload.lotNumber;
        }
        material.isShort = material.consumedQty < material.requiredQty;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchWorkCenters
      .addCase(fetchWorkCenters.fulfilled, (state, action) => {
        state.workCenters = action.payload;
      })
      .addCase(fetchWorkCenters.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to load work centers';
      })
      // loginOperator
      .addCase(loginOperator.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginOperator.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOperator = action.payload;
      })
      .addCase(loginOperator.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Login failed';
      })
      // fetchAssignedWorkOrders
      .addCase(fetchAssignedWorkOrders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssignedWorkOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.assignedWorkOrders = action.payload;
      })
      .addCase(fetchAssignedWorkOrders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || 'Failed to fetch work orders';
      })
      // loadOperationContext
      .addCase(loadOperationContext.fulfilled, (state, action) => {
        state.materialRequirements = action.payload.materials;
        state.qualityPlan = action.payload.plan;
        state.qualityCheckpoints = action.payload.checkpoints;
        state.effectiveOperationId = action.payload.effectiveOperationId;
        state.gauges = action.payload.gauges;
        state.workInstructionSteps = action.payload.steps;
        if (action.payload.gauges.length > 0 && !state.selectedGaugeCode) {
          state.selectedGaugeCode = action.payload.gauges[0].gaugeCode;
        }
      })
      .addCase(loadOperationContext.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to load operation data';
      })
      // startOperation
      .addCase(startOperation.fulfilled, (state, action) => {
        const { opId, executionId } = action.payload;
        state.activeExecutionId = executionId || null;
        const wo = state.assignedWorkOrders.find((w) => w.id === opId);
        if (wo) {
          wo.status = 'started';
          wo.startedAt = new Date().toISOString();
        }
        if (state.activeOperation && state.activeOperation.id === opId) {
          state.activeOperation.status = 'started';
          state.activeOperation.startedAt = new Date().toISOString();
        }
        state.isTimerRunning = true;
        state.operationElapsedSeconds = 0;
        state.gateBlockers = [];
      })
      .addCase(startOperation.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to start operation';
      })
      // pauseOperation
      .addCase(pauseOperation.fulfilled, (state, action) => {
        const { opId } = action.payload;
        const wo = state.assignedWorkOrders.find((w) => w.id === opId);
        if (wo) {
          wo.status = 'paused';
          wo.pausedAt = new Date().toISOString();
        }
        if (state.activeOperation && state.activeOperation.id === opId) {
          state.activeOperation.status = 'paused';
          state.activeOperation.pausedAt = new Date().toISOString();
        }
        state.isTimerRunning = false;
      })
      .addCase(pauseOperation.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to pause operation';
      })
      // completeOperation
      .addCase(completeOperation.fulfilled, (state, action) => {
        const { opId, producedQty, scrapQty, reworkQty } = action.payload;
        const wo = state.assignedWorkOrders.find((w) => w.id === opId);
        if (wo) {
          wo.status = 'completed';
          wo.producedQty = producedQty;
          wo.scrapQty = scrapQty;
          wo.reworkQty = reworkQty;
          wo.completedAt = new Date().toISOString();
        }
        state.activeOperation = null;
        state.activeExecutionId = null;
        state.isTimerRunning = false;
        state.operationElapsedSeconds = 0;
        state.gateBlockers = [];
      })
      .addCase(completeOperation.rejected, (state, action) => {
        const message = (action.payload as string) || 'Failed to complete operation';
        // Quality-gate refusals are shown as actionable blockers, not generic errors
        if (message.toLowerCase().includes('quality gate') || message.toLowerCase().includes('checkpoint')) {
          state.gateBlockers = [message];
        } else {
          state.error = message;
        }
      })
      // updateProductionProgress
      .addCase(updateProductionProgress.fulfilled, (state, action) => {
        const { opId, producedQty, scrapQty, reworkQty } = action.payload;
        const wo = state.assignedWorkOrders.find((w) => w.id === opId);
        if (wo) {
          wo.producedQty = producedQty;
          wo.scrapQty = scrapQty;
          wo.reworkQty = reworkQty;
        }
        if (state.activeOperation && state.activeOperation.id === opId) {
          state.activeOperation.producedQty = producedQty;
          state.activeOperation.scrapQty = scrapQty;
          state.activeOperation.reworkQty = reworkQty;
        }
      })
      .addCase(updateProductionProgress.rejected, (state, action) => {
        state.error = (action.payload as string) || 'Failed to save progress';
      })
      // submitQualityInspection
      .addCase(submitQualityInspection.pending, (state) => {
        state.isSubmittingQuality = true;
        state.error = null;
      })
      .addCase(submitQualityInspection.fulfilled, (state, action) => {
        state.isSubmittingQuality = false;
        state.lastInspection = action.payload;
        state.gateBlockers = [];
      })
      .addCase(submitQualityInspection.rejected, (state, action) => {
        state.isSubmittingQuality = false;
        state.error = (action.payload as string) || 'Failed to submit inspection';
      });
  },
});

export const {
  clearError,
  logoutOperator,
  selectWorkOrder,
  clearActiveOperation,
  tickTimer,
  resetTimer,
  setSelectedGauge,
  clearGateBlockers,
  updateQualityCheckpoint,
  toggleWorkInstructionStep,
  consumeMaterial,
} = operatorSlice.actions;

export default operatorSlice.reducer;

export const QUALITY_REPORT_URL = (inspectionId: string) =>
  `${API_BASE_URL}/quality/inspection-points/${inspectionId}/report`;
